/*
 * export.ts — download helpers
 */

import { render } from './render'
import { primeMath } from './render/math'
import { hydrateMermaid } from './render/mermaid'
import { hydrateCode } from './render/code'
import { showToast } from '../state/toast.svelte'

function download(filename: string, mime: string, content: BlobPart): void {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

function safeFilename(title: string): string {
  return title.replace(/[^\w\s-]/g, '').replace(/\s+/g, '-').slice(0, 60) || 'document'
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

function collectStyles(): string {
  const parts: string[] = []
  for (const sheet of Array.from(document.styleSheets)) {
    try {
      for (const r of Array.from(sheet.cssRules)) parts.push(r.cssText)
    } catch {
      /* cross-origin */
    }
  }
  return parts.join('\n')
}

export function exportMarkdown(source: string, title: string): void {
  download(`${safeFilename(title)}.md`, 'text/markdown', source)
  showToast('Markdown exported')
}

export async function exportHtml(source: string, title: string): Promise<void> {
  if (/\$|```math/.test(source)) await primeMath()
  const r = render(source)
  const mermaidTheme = document.documentElement.dataset['mode'] === 'dark' ? 'dark' : 'neutral'

  const container = document.createElement('article')
  container.className = 'prose'
  container.style.display = 'none'
  container.innerHTML = r.html
  document.body.appendChild(container)
  try {
    await hydrateMermaid(container, r.mermaidDiagrams, r.mermaidNonce, { theme: mermaidTheme })
    await hydrateCode(container)
  } finally {
    container.style.removeProperty('display')
  }
  const bodyHtml = container.outerHTML
  container.remove()

  const html = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8" />
<title>${escapeHtml(title)}</title>
<style>${collectStyles()}</style>
</head>
<body>${bodyHtml}</body>
</html>`
  download(`${safeFilename(title)}.html`, 'text/html', html)
  showToast('HTML exported')
}

export function printDoc(): void {
  window.print()
}
